import { formatCopy } from "../../theme/copy";
import { useRef, useState } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";
import { Box, Button } from "@mui/material";
import { MuiMessage } from "../../components/ui/MuiMessage";
import type { Message } from "../../session/messages";
import { inputIndexNumber, inputListConatiner } from "./styles";
import { inputWrapper } from "../../components/ui/CommonStyles/commonStyle";
import CommonTextInput from "../../components/ui/CommonTextInput";
import { Hints } from "../../components/ui";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import type { TextListDefinition, TextListDraft, TextListEvent } from "./core";

type TextListViewProps = QuestionViewProps<
  TextListDefinition,
  TextListDraft,
  TextListEvent
>;

export const SequentialTextListView = (props: TextListViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <Box data-testid="text-list-sequential">
      {props.definition.rows
        .slice(0, props.draft.activeIndex)
        .map((row, index) => {
          const message: Message<string> = {
            type: "text",
            content: `#${index + 1}/${props.definition.rows.length}: ${props.draft.values[index]}`,
            self: true,
          };
          return (
            <Box
              key={row.id}
              sx={{ display: "flex", alignItems: "center", width: "100%" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MuiMessage
                  id={`text-list-row-${index}`}
                  message={message}
                  showDate={false}
                  showTime={false}
                />
              </Box>
              <Button
                onClick={() => props.dispatch({ type: "activate-row", index })}
              >
                {formatCopy(theme.copy.actions.editResponse, {
                  label: row.label,
                })}
              </Button>
            </Box>
          );
        })}
    </Box>
  );
};

export const SimultaneousTextListView = (props: TextListViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const [, setFocused] = useState(false);
  const inputRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const handleChange = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    index?: number,
  ) => {
    if (index === undefined) return;
    props.dispatch({ type: "set-row", index, value: event.target.value });
  };
  const handleKeyDown = (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
    index?: number,
  ) => {
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing
    )
      return;
    event.preventDefault();
    if (index === props.definition.rows.length - 1) {
      props.controls.primaryAction?.run();
    } else if (index !== undefined) {
      inputRefs.current[index + 1]?.focus();
    }
  };
  return (
    <Box data-testid="text-list-simultaneous" sx={{ width: "100%" }}>
      <Hints text={theme.copy.hints.multitext} variant="hint" />
      {props.definition.rows.map((row, index) => (
        <Box key={row.id} sx={inputListConatiner}>
          <Box sx={inputIndexNumber(theme)}>{row.label}</Box>
          <Box sx={inputWrapper(true, theme)}>
            <CommonTextInput
              isType="text_list"
              isLandScapeMode
              handleKeyDown={handleKeyDown}
              handleChange={handleChange}
              value={props.draft.values[index]}
              index={index}
              inputRefs={inputRefs}
              source={`multi-text-input-field-${index + 1}`}
              customPlaceholder={theme.copy.hints.others}
              accessibleLabel={formatCopy(theme.copy.labels.response, {
                label: row.label,
              })}
              setKeyBoardOpenClose={setFocused}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
};
